import React, { useCallback, useContext, useState, useEffect } from "react";
import { connect } from "react-redux";

import { Dispatch, bindActionCreators } from "redux";

import { ActionBar, Sticky } from "@plex/react-components";

import { INotification, notification } from "infrastructure/notification";
import { DialogNavigationContext, ActionBarLink } from "ui/app/navigation";
import { useLocalizedValues, useUserContext } from "ui/hooks";
import { glossaryMessages } from "ui/screens/plex-dictionary/plex-dictionary.localization";

import { translationsManagerActions } from "../TranslationsManager.actions";
import { ITranslationsManagerState, ITermTranslationRow } from "../TranslationsManager.state";

import { TermTranslationsUpload, TermTranslationsUploadResult } from "./term-translations-upload";

const useNullableCallback: (callback?: () => void) => () => void = callback => {
  return useCallback(() => {
    callback && callback();
  }, [callback]);
};

export interface ITranslationsManagerActionBarProps {
  approveTermTranslations?: () => void;
  obsoleteTermTranslations?: () => void;
  downloadTermTranslationsUploadTemplate?: () => void;
  exportToXml?: () => void;
  isExportDisabled?: boolean;
  onTermTranslationsUploadCompleted?: (notification: INotification) => void;
  selectedRows?: ITermTranslationRow[];
}

export const TranslationsManagerActionBar: React.FC<ITranslationsManagerActionBarProps> = ({
  approveTermTranslations,
  obsoleteTermTranslations,
  downloadTermTranslationsUploadTemplate,
  exportToXml,
  isExportDisabled,
  onTermTranslationsUploadCompleted,
  selectedRows
}) => {
  const dialogNavigationContext = useContext(DialogNavigationContext);

  const inReviewStatus = glossaryMessages.option("termTranslationStatusInReview").value;
  const localizedValues = useLocalizedValues(() => ({
    termTranslationsUploadTitle: glossaryMessages.title("termTranslationsUpload"),
    termTranslationsUploadAction: glossaryMessages.action("termTranslationsUpload"),
    selectTermTranslationsUploadFileAction: glossaryMessages.action("selectTermTranslationsUploadFile"),
    downloadTranslationsFileTemplateAction: glossaryMessages.action("downloadTranslationsFileTemplate"),
    exportTranslationsAction: glossaryMessages.action("exportTranslations"),
    termTranslationsApproveAction: glossaryMessages.action("termTranslationsApprove"),
    termTranslationsObsoleteAction: glossaryMessages.action("termTranslationsObsolete"),
    termTranslationsAllTranslationsAction: glossaryMessages.action("allTranslations")
  }));

  const exportExecute = useNullableCallback(exportToXml);
  const approveExecute = useNullableCallback(approveTermTranslations);
  const obsoleteExecute = useNullableCallback(obsoleteTermTranslations);
  const downloadTermTranslationsUploadTemplateExecute = useNullableCallback(downloadTermTranslationsUploadTemplate);

  const getButtonsStatus = useCallback(
    () => ({
      isApproveDisabled:
        !selectedRows ||
        selectedRows.length === 0 ||
        selectedRows.some(s => s.data.translationStatus !== inReviewStatus || s.isTranslationChanged || s.isTranslationDefinitionChanged),
      isObsoleteDisabled: !selectedRows || selectedRows.length === 0 || selectedRows.some(s => !s.data.translationStatus || !s.data.translation || !s.data.translationDefinition),
      isViewTranslationsDisabled: !selectedRows || selectedRows.length !== 1
    }),
    [inReviewStatus, selectedRows]
  );

  const userContext = useUserContext();
  const roles = userContext.getRoleAccessData();

  const [buttons, setButtonsState] = useState<{ isApproveDisabled: boolean; isObsoleteDisabled: boolean; isViewTranslationsDisabled: boolean }>(() => ({
    ...getButtonsStatus()
  }));

  useEffect(() => {
    setButtonsState(() => ({
      ...getButtonsStatus()
    }));
  }, [getButtonsStatus]);

  return (
    <Sticky>
      <ActionBar>
        {(roles.PlexLocalizationManager || roles.Translator) && (
          <ActionBar.Dropdown>
            <ActionBar.DropdownButton text={localizedValues.termTranslationsUploadAction} title={localizedValues.termTranslationsUploadTitle} />
            <ActionBar.DropdownItems>
              <ActionBarLink
                text={localizedValues.selectTermTranslationsUploadFileAction}
                title={localizedValues.selectTermTranslationsUploadFileAction}
                dialog={{
                  content: (
                    <TermTranslationsUpload
                      onUploadCompleted={error => {
                        if (onTermTranslationsUploadCompleted) {
                          if (error) {
                            dialogNavigationContext.showDialog({
                              content: <TermTranslationsUploadResult />
                            });
                          } else {
                            onTermTranslationsUploadCompleted(notification.success().useMessage({ messageId: glossaryMessages.info("termTranslationsUploadCompleted") }));
                          }
                        }
                      }}
                    />
                  )
                }}
              />
              <ActionBar.Divider />
              <ActionBar.Button
                text={localizedValues.downloadTranslationsFileTemplateAction}
                title={localizedValues.downloadTranslationsFileTemplateAction}
                onClick={downloadTermTranslationsUploadTemplateExecute}
              />
            </ActionBar.DropdownItems>
          </ActionBar.Dropdown>
        )}
        <ActionBar.Button text={localizedValues.exportTranslationsAction} title={localizedValues.exportTranslationsAction} onClick={exportExecute} disabled={isExportDisabled} />
        {roles.PlexLocalizationManager && (
          <ActionBar.Button
            onClick={approveExecute}
            disabled={buttons.isApproveDisabled}
            text={localizedValues.termTranslationsApproveAction}
            title={localizedValues.termTranslationsApproveAction}
          />
        )}
        {(roles.PlexLocalizationManager || roles.Translator) && (
          <ActionBar.Button
            onClick={obsoleteExecute}
            disabled={buttons.isObsoleteDisabled}
            text={localizedValues.termTranslationsObsoleteAction}
            title={localizedValues.termTranslationsObsoleteAction}
          />
        )}
        <ActionBarLink
          openInDialog
          disabled={buttons.isViewTranslationsDisabled}
          text={localizedValues.termTranslationsAllTranslationsAction}
          href={`/terms/${selectedRows && selectedRows[0] && selectedRows[0].data.termId}/translations`}
          title={localizedValues.termTranslationsAllTranslationsAction}
        />
      </ActionBar>
    </Sticky>
  );
};

/* istanbul ignore next */
export default connect(
  (state: { translationsManager: ITranslationsManagerState }) => {
    return {
      isExportDisabled: state.translationsManager.isExportDisabled,
      selectedRows: state.translationsManager.grid.selected
    };
  },
  (dispatch: Dispatch) => {
    return bindActionCreators(
      {
        exportToXml: translationsManagerActions.exportTranslationsToXml,
        onTermTranslationsUploadCompleted: translationsManagerActions.onTermTranslationUploadCompleted,
        downloadTermTranslationsUploadTemplate: translationsManagerActions.downloadTermTranslationsUploadTemplate,
        approveTermTranslations: translationsManagerActions.approveTermTranslations,
        obsoleteTermTranslations: translationsManagerActions.obsoleteTermTranslations
      },
      dispatch
    );
  }
)(TranslationsManagerActionBar);
